import React from 'react';

// Compute weather-based tip from today's forecast
function getWeatherTip(weather, lang) {
  if (!weather?.length) return null;
  const today = weather[0];
  const hi = today?.hi ?? today?.high ?? today?.temp ?? null;
  const icon = today?.icon || today?.condition || '';
  const isRain = /rain|thunder|storm|drizzle|shower/i.test(icon);
  const isSnow = /snow/i.test(icon);
  const isHot  = hi >= 33;
  const isCold = hi !== null && hi < 12;
  const isWind = /wind/i.test(icon);

  const MSG = {
    zh: {
      rain: '🌧️ 今天有雨，記得帶傘和防水鞋。',
      snow: '❄️ 今天有雪，記得多穿保暖衣物及防滑鞋。',
      hot:  `☀️ 今天最高 ${hi}°C，建議多補水、防曬，避免正午外出。`,
      cold: `🧥 今天偏涼（${hi}°C），建議多穿一層，帶防風外套。`,
      wind: '💨 今天風大，出行時注意固定好隨身物品。',
      def:  `🌤️ 天氣舒適（${hi ?? ''}°C），是戶外探索的好日子！`,
    },
    en: {
      rain: '🌧️ Rain expected today — bring an umbrella and waterproof shoes.',
      snow: '❄️ Snow forecast — dress in warm layers and wear slip-resistant shoes.',
      hot:  `☀️ Hot day (${hi}°C) — stay hydrated, wear sunscreen, avoid midday sun.`,
      cold: `🧥 Cold today (${hi}°C) — layer up and bring a windproof jacket.`,
      wind: '💨 Windy today — hold on to your hat and secure loose items.',
      def:  `🌤️ Comfortable weather (${hi ?? ''}°C) — a great day to explore outdoors!`,
    },
    ja: {
      rain: '🌧️ 今日は雨の予報です。傘と防水シューズをお持ちください。',
      snow: '❄️ 今日は雪の予報です。暖かい服装と滑りにくい靴でお出かけください。',
      hot:  `☀️ 今日の最高気温は${hi}°Cです。こまめな水分補給と日焼け対策を心がけ、正午前後の外出は控えましょう。`,
      cold: `🧥 今日は肌寒いです（${hi}°C）。重ね着をして、防風ジャケットをお持ちください。`,
      wind: '💨 今日は風が強いです。帽子や持ち物が飛ばされないようご注意ください。',
      def:  `🌤️ 過ごしやすい天気です（${hi ?? ''}°C）。屋外散策にぴったりの一日です！`,
    },
    ko: {
      rain: '🌧️ 오늘은 비 소식이 있어요. 우산과 방수 신발을 챙기세요.',
      snow: '❄️ 오늘은 눈 소식이 있어요. 따뜻하게 껴입고 미끄럼 방지 신발을 신으세요.',
      hot:  `☀️ 오늘 최고 기온은 ${hi}°C예요. 수분을 충분히 섭취하고 자외선 차단제를 바르며 한낮 외출은 피하세요.`,
      cold: `🧥 오늘은 쌀쌀해요 (${hi}°C). 겹겹이 입고 방풍 재킷을 챙기세요.`,
      wind: '💨 오늘은 바람이 강해요. 모자와 소지품이 날아가지 않도록 주의하세요.',
      def:  `🌤️ 날씨가 쾌적해요 (${hi ?? ''}°C). 야외 나들이하기 좋은 날이에요!`,
    },
  };
  const L = MSG[lang] || MSG.zh;
  if (isRain) return L.rain;
  if (isSnow) return L.snow;
  if (isHot)  return L.hot;
  if (isCold) return L.cold;
  if (isWind) return L.wind;
  return L.def;
}

const LABEL = {
  zh: { tip:'今日旅行提示', apps:'必備本地 App' },
  en: { tip:"Today's Travel Tip", apps:'Must-Have Apps' },
  ja: { tip:'今日の旅行アドバイス', apps:'必須アプリ' },
  ko: { tip:'오늘의 여행 팁', apps:'필수 앱' },
};

export function TravelExtras({ info, weather, lang }) {
  const langKey = lang==='zh-CN'?'zh':lang==='en'?'en':lang==='ja'?'ja':lang==='ko'?'ko':'zh';
  const L = LABEL[langKey] || LABEL.zh;

  const tip  = getWeatherTip(weather, langKey);
  const apps = info?.local_apps || '';
  const hasApps = Array.isArray(apps) ? apps.some(a => a && a.name) : !!apps;

  if (!tip && !hasApps) return null;

  return (
    <div style={{ background:'white', borderRadius:16, padding:'14px 16px', marginBottom:12,
      boxShadow:'0 2px 12px rgba(0,0,0,0.07)', border:'1px solid #f1f5f9' }}>
      {tip && (
        <div style={{ background:'#fffbeb', borderRadius:10, padding:'10px 14px', marginBottom: hasApps ? 12 : 0, border:'1px solid #fde68a' }}>
          <p style={{ margin:'0 0 4px', fontSize:11, fontWeight:900, color:'#92400e', textTransform:'uppercase', letterSpacing:1 }}>{L.tip}</p>
          <p style={{ margin:0, fontSize:13, color:'#1e293b', lineHeight:1.6, fontWeight:600 }}>{tip}</p>
        </div>
      )}
      {hasApps && (
        <div>
          <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:900, color:'#64748b', textTransform:'uppercase', letterSpacing:1 }}>{L.apps}</p>
          {Array.isArray(apps) ? (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {apps.filter(a => a && a.name).map((a, i) => (
                <div key={i} style={{ display:'flex', alignItems:'baseline', gap:6, padding:'6px 10px',
                  borderRadius:10, background:'#f1f5f9', border:'1px solid #e2e8f0' }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'#334155', whiteSpace:'nowrap' }}>📱 {a.name}</span>
                  {a.desc && <span style={{ fontSize:11, color:'#64748b' }}>— {a.desc}</span>}
                </div>
              ))}
            </div>
          ) : (
            // Legacy cached entries stored local_apps as a plain comma-separated
            // string with no per-app description — render name-only chips.
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {apps.split(/[,，、]/).map(a => a.trim()).filter(Boolean).map((app, i) => (
                <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px',
                  borderRadius:20, background:'#f1f5f9', border:'1px solid #e2e8f0', fontSize:12, fontWeight:700, color:'#334155' }}>
                  📱 {app}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
